import {
  DashboardAssistantThreadId,
  DashboardAssistantTurnId,
  type DashboardAssistantEvent,
} from "@t3tools/contracts";
import { useQuery } from "@tanstack/react-query";
import { Schema } from "effect";
import { useCallback, useEffect, useReducer, useRef, useState } from "react";

import { setLocalStorageItem, useLocalStorage } from "~/hooks/useLocalStorage";

import { dashboardAssistantThreadQueryOptions } from "../artifactsReactQuery";
import type { DashboardAssistantSource } from "./dashboardAssistantSource";
import {
  EMPTY_DASHBOARD_ASSISTANT_THREAD,
  dashboardAssistantThreadReducer,
  type DashboardAssistantThreadAction,
  type DashboardAssistantThreadState,
} from "./dashboardAssistantThread";

const asThreadId = Schema.decodeSync(DashboardAssistantThreadId);
const asTurnId = Schema.decodeSync(DashboardAssistantTurnId);

const SEND_FAILED = "The assistant didn't get that. Try again in a moment.";

function newId(): string {
  return globalThis.crypto.randomUUID();
}

/**
 * This person's thread for one dashboard. The id is made here and kept in
 * the browser, so reopening the dashboard continues the same thread; the
 * assistant keeps the thread itself.
 */
const threadStorageKey = (dashboardId: string) => `dashboards:assistant-thread:${dashboardId}`;
const ThreadIdStorage = Schema.NullOr(Schema.String);

/**
 * Makes `threadId` the thread a dashboard's page continues. The home page uses
 * it when it hands a build to the new dashboard, so the page keeps listening
 * to the same turn.
 */
export function handOffDashboardAssistantThread(dashboardId: string, threadId: string): void {
  setLocalStorageItem(threadStorageKey(dashboardId), threadId, ThreadIdStorage);
}

export function useDashboardAssistantThreadId(dashboardId: string): string {
  const [stored, setStored] = useLocalStorage<string | null, string | null>(
    threadStorageKey(dashboardId),
    null,
    ThreadIdStorage,
  );
  const [fallback] = useState(() => stored ?? newId());
  useEffect(() => {
    if (stored === null) setStored(fallback);
  }, [stored, setStored, fallback]);
  return stored ?? fallback;
}

export type DashboardAssistantThread = {
  state: DashboardAssistantThreadState;
  send: (text: string) => void;
  cancel: () => void;
  dispatch: (action: DashboardAssistantThreadAction) => void;
};

/**
 * Sends the person's messages and folds this thread's events into its state.
 * `onEvent` sees every event of the thread too, so the page can apply edits
 * and previews to the dashboard.
 */
export function useDashboardAssistantThread(input: {
  source: DashboardAssistantSource | null;
  /** The dashboard being talked about; null before there is one (the home page). */
  dashboardRef: string | null;
  threadId: string;
  onEvent?: (event: DashboardAssistantEvent) => void;
}): DashboardAssistantThread {
  const { source, dashboardRef, threadId } = input;
  const [state, dispatch] = useReducer(
    dashboardAssistantThreadReducer,
    EMPTY_DASHBOARD_ASSISTANT_THREAD,
  );
  const onEvent = useRef(input.onEvent);
  useEffect(() => {
    onEvent.current = input.onEvent;
  });

  useEffect(() => {
    if (!source) return;
    return source.subscribe((event) => {
      if (event.threadId !== threadId) return;
      dispatch({ type: "event", event });
      onEvent.current?.(event);
    });
  }, [source, threadId]);

  // The saved past, replayed once when the page opens.
  const history = useQuery({
    ...dashboardAssistantThreadQueryOptions(dashboardRef ?? ""),
    enabled: dashboardRef !== null,
  });
  useEffect(() => {
    if (history.data && history.data.length > 0) {
      dispatch({ type: "history", timeline: history.data });
    }
  }, [history.data]);

  const { running } = state;

  const send = useCallback(
    (text: string) => {
      const trimmed = text.trim();
      if (!source || !trimmed || running) return;
      const id = newId();
      dispatch({ type: "sent", id, text: trimmed });
      source
        .send({ dashboardRef, threadId: asThreadId(threadId), text: trimmed })
        .then((result) => dispatch({ type: "accepted", turnId: result.turnId }))
        .catch(() => dispatch({ type: "sendFailed", id: `${id}:failed`, message: SEND_FAILED }));
    },
    [source, dashboardRef, threadId, running],
  );

  const cancel = useCallback(() => {
    if (!source || !running) return;
    const { turnId } = running;
    void source
      .cancel({
        threadId: asThreadId(threadId),
        ...(turnId ? { turnId: asTurnId(turnId) } : {}),
      })
      .catch(() => undefined);
  }, [source, threadId, running]);

  return { state, send, cancel, dispatch };
}
